import type { OrderChangeHistoryItem } from '@/api/hooks/useOrderHistory'
import { ChangeRow } from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/OrderChangeSummary/components/ChangeRow'
import { LoadCard } from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/OrderChangeSummary/components/LoadCard'
import {
  computeDropChanges,
  formatDeliveryTime,
  formatTimestamp,
  getChangeTypeLabel,
  getMappingDisplay,
  hasDeliveryWindowChange,
  matchDrops,
} from '@/pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/OrderChangeSummary/utils'
import {
  ArrowRightOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, theme } from 'antd'
import { ChevronDown, ChevronUp, Clock, MapPin, Truck } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'

// Icons for change types
const CHANGE_TYPE_ICONS: Record<string, React.ReactNode> = {
  supply_change: <Truck size={12} />,
  destination_change: <MapPin size={12} />,
  delivery_window_change: <Clock size={12} />,
}

// Helper to check if a value is empty/null/undefined
const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || value === '—'

// Helper to check if a change row should be shown (at least one value is meaningful)
const hasValue = (before: unknown, after: unknown): boolean =>
  !isEmpty(before) || !isEmpty(after)

// Helper to get status color based on status flags
function getStatusColor(
  isError: boolean,
  isRequested: boolean,
  token: { colorError: string; colorInfo: string; colorWarning: string },
): string {
  if (isError) return token.colorError
  if (isRequested) return token.colorInfo
  return token.colorWarning
}

// Helper to get status label based on status flags
function getStatusLabel(isRequested: boolean, isRejected: boolean): string {
  if (isRequested) return 'Requested'
  if (isRejected) return 'Rejected'
  return 'Error'
}

interface OrderChangeCardProps {
  change: OrderChangeHistoryItem
  defaultExpanded?: boolean
  hideTimeline?: boolean
}

export function OrderChangeCard({
  change,
  defaultExpanded = false,
  hideTimeline = false,
}: OrderChangeCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const { token } = theme.useToken()
  const isError = change.status === 'error'
  const isRejected = change.status === 'rejected'
  const isRequested = change.status === 'requested'
  const statusColor = getStatusColor(isError, isRequested, token)

  const { before_change: beforeChange, after_change: afterChange } = change

  // Check what types of changes exist for expanded content
  const hasDeliveryWindow = hasDeliveryWindowChange(
    beforeChange?.delivery_window,
    afterChange?.delivery_window,
  )

  const matchedDrops = matchDrops(beforeChange?.drops, afterChange?.drops)
  const hasDropChanges = matchedDrops.some((matched) => {
    const changes = computeDropChanges(matched.before, matched.after)
    return changes.totalCount > 0
  })

  const hasNote = beforeChange?.note !== afterChange?.note
  const hasRejectionReason = isRejected && !!change.rejected_reason_code
  const hasSubmitReason = !!change.submit_reason_code
  const hasAcceptReason = !!change.accept_reason_code

  const hasChangeDetails =
    hasDeliveryWindow ||
    hasDropChanges ||
    hasNote ||
    hasRejectionReason ||
    hasSubmitReason ||
    hasAcceptReason
  const canExpand = hasChangeDetails

  return (
    <Horizontal gap={hideTimeline ? 0 : 12} style={{ position: 'relative' }}>
      {/* Timeline dot */}
      {!hideTimeline && (
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor:
              isError || isRejected || isRequested
                ? statusColor
                : token.colorTextQuaternary,
            flexShrink: 0,
            marginTop: 18,
            zIndex: 1,
          }}
        />
      )}

      {/* Card content */}
      <Card
        size='small'
        style={{
          flex: 1,
          cursor: canExpand ? 'pointer' : 'default',
          transition: 'background-color 0.2s',
          borderLeft:
            isError || isRejected || isRequested
              ? `3px solid ${statusColor}`
              : undefined,
        }}
        styles={{
          body: { padding: 0 },
        }}
        onClick={() => canExpand && setExpanded(!expanded)}
      >
        {/* Header section with optional tint */}
        <div
          style={{
            padding: 12,
            backgroundColor:
              isError || isRejected || isRequested
                ? `color-mix(in srgb, ${statusColor} 5%, transparent)`
                : undefined,
          }}
        >
          <Vertical gap={8}>
            {/* Row 1: Order Updated + Timestamp + Chevron */}
            <Horizontal justifyContent='space-between' alignItems='center'>
              <Horizontal gap={8} alignItems='center'>
                {isRequested ? (
                  <ClockCircleOutlined
                    style={{ color: token.colorTextSecondary, display: 'flex' }}
                  />
                ) : (
                  <EditOutlined
                    style={{ color: token.colorTextSecondary, display: 'flex' }}
                  />
                )}
                <Texto weight='600'>
                  {isRequested ? 'Order Change Request' : 'Order Updated'}
                </Texto>
              </Horizontal>
              <Horizontal gap={8} alignItems='center'>
                <Texto appearance='hint' style={{ fontSize: 12 }}>
                  {formatTimestamp(change.created_on)}
                </Texto>
                {canExpand &&
                  (expanded ? (
                    <ChevronUp size={16} color={token.colorTextTertiary} />
                  ) : (
                    <ChevronDown size={16} color={token.colorTextTertiary} />
                  ))}
              </Horizontal>
            </Horizontal>

            {/* Row 2: Direction (bold) + Approved/Rejected by */}
            <Horizontal justifyContent='space-between' alignItems='center'>
              <Horizontal gap={6} alignItems='center'>
                <Texto weight='600' style={{ fontSize: 13 }}>
                  {change.origin_tenant_name}
                </Texto>
                <ArrowRightOutlined
                  style={{ fontSize: 10, color: token.colorTextTertiary }}
                />
                <Texto weight='600' style={{ fontSize: 13 }}>
                  {change.destination_tenant_name ?? 'N/A'}
                </Texto>
              </Horizontal>
              {change.approved_by && (
                <Texto appearance='hint' style={{ fontSize: 12 }}>
                  Approved by {change.approved_by}
                </Texto>
              )}
              {change.rejected_by && (
                <Texto appearance='hint' style={{ fontSize: 12 }}>
                  Rejected by {change.rejected_by}
                </Texto>
              )}
            </Horizontal>

            {/* Row 3: Change type indicators + Error/Rejected/Requested indicator */}
            {(isError ||
              isRejected ||
              isRequested ||
              (change.change_types && change.change_types.length > 0)) && (
              <Horizontal justifyContent='space-between' alignItems='center'>
                <Horizontal gap={12} alignItems='center'>
                  {change.change_types?.map((type) => (
                    <Horizontal key={type} gap={4} alignItems='center'>
                      <span
                        style={{
                          color: token.colorTextTertiary,
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 16,
                        }}
                      >
                        {CHANGE_TYPE_ICONS[type]}
                      </span>
                      <Texto
                        appearance='hint'
                        style={{ fontSize: 12, lineHeight: '16px' }}
                      >
                        {getChangeTypeLabel(type)}
                      </Texto>
                    </Horizontal>
                  ))}
                </Horizontal>
                {(isError || isRejected || isRequested) && (
                  <Horizontal gap={4} alignItems='center'>
                    {isError && (
                      <CloseCircleOutlined
                        style={{ fontSize: 12, color: statusColor }}
                      />
                    )}
                    {isRejected && (
                      <StopOutlined
                        style={{ fontSize: 12, color: statusColor }}
                      />
                    )}
                    {isRequested && (
                      <ClockCircleOutlined
                        style={{ fontSize: 12, color: statusColor }}
                      />
                    )}
                    <Texto style={{ fontSize: 12, color: statusColor }}>
                      {getStatusLabel(isRequested, isRejected)}
                      {isRejected &&
                        change.rejected_reason_code &&
                        `: ${change.rejected_reason_code.length > 15 ? `${change.rejected_reason_code.slice(0, 15)}...` : change.rejected_reason_code}`}
                    </Texto>
                  </Horizontal>
                )}
              </Horizontal>
            )}
          </Vertical>
        </div>

        {/* Expanded Content - flat inline rendering */}
        {expanded && hasChangeDetails && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              padding: 12,
              paddingTop: 12,
              cursor: 'default',
            }}
          >
            <Vertical gap={16}>
              {/* Delivery Window Section */}
              {hasDeliveryWindow && (
                <Vertical gap={8}>
                  <Texto weight='600' style={{ fontSize: 13 }}>
                    Delivery Window
                  </Texto>
                  {(() => {
                    const beforeStart = beforeChange?.delivery_window
                      ? formatDeliveryTime(beforeChange.delivery_window.start)
                      : null
                    const beforeEnd = beforeChange?.delivery_window
                      ? formatDeliveryTime(beforeChange.delivery_window.end)
                      : null
                    const afterStart = afterChange?.delivery_window
                      ? formatDeliveryTime(afterChange.delivery_window.start)
                      : null
                    const afterEnd = afterChange?.delivery_window
                      ? formatDeliveryTime(afterChange.delivery_window.end)
                      : null

                    const formatDT = (
                      t: { date: string; time: string } | null,
                    ) => (t ? `${t.date} ${t.time}` : '—')

                    const startChanged =
                      formatDT(beforeStart) !== formatDT(afterStart)
                    const endChanged =
                      formatDT(beforeEnd) !== formatDT(afterEnd)

                    return (
                      <Vertical gap={4}>
                        {startChanged && (
                          <ChangeRow
                            label='Start'
                            beforeValue={formatDT(beforeStart)}
                            afterValue={formatDT(afterStart)}
                          />
                        )}
                        {endChanged && (
                          <ChangeRow
                            label='End'
                            beforeValue={formatDT(beforeEnd)}
                            afterValue={formatDT(afterEnd)}
                          />
                        )}
                      </Vertical>
                    )
                  })()}
                </Vertical>
              )}

              {/* Drops Section - flattened */}
              {matchedDrops
                .map((matched, index) => {
                  const changes = computeDropChanges(
                    matched.before,
                    matched.after,
                  )
                  if (changes.totalCount === 0) return null

                  const siteName =
                    getMappingDisplay(matched.after?.site) ||
                    getMappingDisplay(matched.before?.site) ||
                    'Unknown Site'

                  return (
                    <Vertical key={index} gap={8}>
                      <Texto weight='600' style={{ fontSize: 13 }}>
                        Drop {index + 1} — {siteName}
                      </Texto>

                      {/* Destination changes */}
                      {changes.destination
                        .filter((change) =>
                          hasValue(change.before, change.after),
                        )
                        .map((change, i) => (
                          <ChangeRow
                            key={`dest-${i}`}
                            label={change.field}
                            beforeValue={change.before}
                            afterValue={change.after}
                          />
                        ))}

                      {/* Product & Volume changes */}
                      {changes.productVolume
                        .filter((change) =>
                          hasValue(change.before, change.after),
                        )
                        .map((change, i) => (
                          <ChangeRow
                            key={`pv-${i}`}
                            label={change.field}
                            beforeValue={change.before}
                            afterValue={change.after}
                          />
                        ))}

                      {/* Loads Section */}
                      {(() => {
                        const beforeLoads = matched.before?.loads || []
                        const afterLoads = matched.after?.loads || []
                        const maxLen = Math.max(
                          beforeLoads.length,
                          afterLoads.length,
                        )

                        if (maxLen === 0) return null

                        // Build a lookup for load changes by index
                        const loadChangesMap = new Map(
                          changes.loads.map((lc) => [lc.loadIndex, lc.fields]),
                        )

                        // Get drop-level product as fallback
                        const dropProduct =
                          getMappingDisplay(matched.after?.product) ||
                          getMappingDisplay(matched.before?.product)

                        return (
                          <Vertical gap={8}>
                            <Texto
                              weight='600'
                              style={{
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: token.colorTextTertiary,
                                marginTop: 8,
                              }}
                            >
                              Loads
                            </Texto>
                            <Vertical gap={8}>
                              {Array.from({ length: maxLen }, (_, i) => (
                                <LoadCard
                                  key={i}
                                  loadIndex={i}
                                  beforeLoad={beforeLoads[i] || null}
                                  afterLoad={afterLoads[i] || null}
                                  changes={loadChangesMap.get(i) || []}
                                  fallbackProduct={dropProduct}
                                />
                              ))}
                            </Vertical>
                          </Vertical>
                        )
                      })()}
                    </Vertical>
                  )
                })
                .filter(Boolean)}

              {/* Note Section */}
              {hasNote && hasValue(beforeChange?.note, afterChange?.note) && (
                <Vertical gap={8}>
                  <Texto weight='600' style={{ fontSize: 13 }}>
                    Note
                  </Texto>
                  <ChangeRow
                    label='Note'
                    beforeValue={beforeChange?.note}
                    afterValue={afterChange?.note}
                  />
                </Vertical>
              )}

              {/* Submission Reason Section */}
              {hasSubmitReason && (
                <Vertical gap={8}>
                  <Texto weight='600' style={{ fontSize: 13 }}>
                    Submission Reason
                  </Texto>
                  <Texto style={{ color: token.colorTextSecondary }}>
                    {change.submit_reason_code}
                  </Texto>
                </Vertical>
              )}

              {/* Acceptance Reason Section */}
              {hasAcceptReason && (
                <Vertical gap={8}>
                  <Texto weight='600' style={{ fontSize: 13 }}>
                    Acceptance Reason
                  </Texto>
                  <Texto style={{ color: token.colorSuccess }}>
                    {change.accept_reason_code}
                  </Texto>
                </Vertical>
              )}

              {/* Rejection Reason Section */}
              {hasRejectionReason && (
                <Vertical gap={8}>
                  <Texto weight='600' style={{ fontSize: 13 }}>
                    Rejection Reason
                  </Texto>
                  <Texto style={{ color: statusColor }}>
                    {change.rejected_reason_code}
                  </Texto>
                </Vertical>
              )}
            </Vertical>
          </div>
        )}
      </Card>
    </Horizontal>
  )
}
